import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tooltip,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { ICrmBrandContactDetailsData, useUpdateContactBrandApi } from 'apis/crm-brand-contact-details';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiEdit2, FiRefreshCw, FiTrash2 } from 'react-icons/fi';

export interface IActionsCell {
  contactDetails: ICrmBrandContactDetailsData;
  onEdit: (contactDetails: ICrmBrandContactDetailsData) => void;
  onDelete: (contactDetails: ICrmBrandContactDetailsData) => void;
}

interface BrandChangeForm {
  brand: { value: number; label: string } | null;
}

export const ActionsCell: React.FC<IActionsCell> = ({ contactDetails, onEdit, onDelete }) => {
  const { isOpen: isBrandModalOpen, onOpen: onBrandModalOpen, onClose: onBrandModalClose } = useDisclosure();

  // Self-contained mutation for updating brand
  const { mutate: updateContactBrand, isLoading: isUpdatingBrand } = useUpdateContactBrandApi({
    onSuccess: () => {
      onBrandModalClose();
      reset();
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<BrandChangeForm>({
    mode: 'onChange',
    defaultValues: { brand: null },
  });

  // Async load brands for selection
  const loadBrandOptions = useCallback(async (inputValue: string) => {
    try {
      const { data } = await http.get('ums/v1/crm/management/brands', {
        params: {
          page: 1,
          limit: 50,
          name: inputValue || undefined,
        },
      });
      return (data?.data || []).map((brand: any) => ({
        value: brand.id,
        label: brand.name,
      }));
    } catch (error) {
      console.error('Error loading brands:', error);
      return [];
    }
  }, []);

  // Handle brand change submission
  const onSubmitBrandChange = useCallback(
    (data: BrandChangeForm) => {
      if (!data.brand?.value) return;

      updateContactBrand({
        id: contactDetails.id,
        brandId: data.brand.value,
      });
    },
    [updateContactBrand, contactDetails.id]
  );

  // Memoized handlers
  const handleEdit = useCallback(() => {
    onEdit(contactDetails);
  }, [onEdit, contactDetails]);

  const handleDelete = useCallback(() => {
    onDelete(contactDetails);
  }, [onDelete, contactDetails]);

  const handleBrandChange = useCallback(() => {
    reset({ brand: null });
    onBrandModalOpen();
  }, [reset, onBrandModalOpen]);

  const handleBrandModalClose = useCallback(() => {
    reset();
    onBrandModalClose();
  }, [reset, onBrandModalClose]);

  return (
    <>
      <HStack spacing={1}>
        <Tooltip label='Edit Contact Details' placement='top'>
          <IconButton
            aria-label='Edit contact details'
            icon={<FiEdit2 />}
            size='sm'
            variant='ghost'
            colorScheme='blue'
            onClick={handleEdit}
          />
        </Tooltip>

        <Tooltip label='Change Brand' placement='top'>
          <IconButton
            aria-label='Change brand'
            icon={<FiRefreshCw />}
            size='sm'
            variant='ghost'
            colorScheme='orange'
            onClick={handleBrandChange}
          />
        </Tooltip>

        <Tooltip label='Delete Contact Details' placement='top'>
          <IconButton
            aria-label='Delete contact details'
            icon={<FiTrash2 />}
            size='sm'
            variant='ghost'
            colorScheme='red'
            onClick={handleDelete}
          />
        </Tooltip>
      </HStack>

      {/* Brand Change Modal */}
      <Modal isOpen={isBrandModalOpen} onClose={handleBrandModalClose} size='md'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Brand</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} as='form' onSubmit={handleSubmit(onSubmitBrandChange)}>
              <VStack spacing={2} align='start' bg='gray.50' p={4} borderRadius='md' w='full'>
                <HStack spacing={2}>
                  <strong>Contact:</strong>
                  <span>{contactDetails.contactName}</span>
                </HStack>
                <HStack spacing={2}>
                  <strong>Current Brand:</strong>
                  <span>{contactDetails.brand?.name}</span>
                </HStack>
              </VStack>

              <FormControl isRequired isInvalid={!!errors.brand}>
                <FormLabel>Select New Brand</FormLabel>
                <Controller
                  name='brand'
                  control={control}
                  rules={{ required: 'Please select a brand' }}
                  render={({ field }) => (
                    <ChakraAsyncSelect
                      {...field}
                      loadOptions={loadBrandOptions}
                      placeholder='Search and select a brand...'
                      isClearable
                      defaultOptions
                      cacheOptions
                    />
                  )}
                />
                {errors.brand && <span style={{ color: 'red', fontSize: '14px' }}>{errors.brand.message}</span>}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button variant='outline' onClick={handleBrandModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme='blue'
                onClick={handleSubmit(onSubmitBrandChange)}
                isLoading={isUpdatingBrand}
                isDisabled={!isValid}
                loadingText='Updating...'>
                Update Brand
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
